import { useEffect, useRef } from 'react';

// Hardware scanners type a whole barcode in a few ms; people don't
const MAX_KEY_GAP_MS = 60;
const MIN_LENGTH = 4;
// Some scanners send no Enter at the end; submit after this much silence
const IDLE_SUBMIT_MS = 150;

function isTextField(target) {
  const tag = target?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
}

/**
 * Catch a hardware scan anywhere on the page, so the next item can be
 * scanned straight from the result or quantity screen without first tapping
 * "scan again". Slow typing is ignored, and so are keys aimed at a text field.
 */
export function useScanCapture(onScan, { enabled = true } = {}) {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;
    let buffer = '';
    let last = 0;
    let idleTimer = null;

    const flush = () => {
      clearTimeout(idleTimer);
      const code = buffer;
      buffer = '';
      if (code.length >= MIN_LENGTH) onScanRef.current(code);
    };

    const handleKeyDown = (e) => {
      if (isTextField(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
      const now = Date.now();
      if (e.key === 'Enter') {
        if (buffer.length >= MIN_LENGTH && now - last < MAX_KEY_GAP_MS * 3) {
          e.preventDefault();
          flush();
        }
        buffer = '';
        return;
      }
      if (e.key.length !== 1) return;
      buffer = now - last <= MAX_KEY_GAP_MS ? buffer + e.key : e.key;
      last = now;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (buffer.length >= MIN_LENGTH * 2) flush();
        else buffer = '';
      }, IDLE_SUBMIT_MS);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(idleTimer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);
}
